import {
  NotificationOption,
  NotificationType,
} from 'features/notifications/notifications.types';
import { Topic } from 'shared/types';
import { GenericGeoEvent } from 'features/geoEvents/geoEvent.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useTopicSubscriptions } from 'features/notifications/hooks/useTopicSubscriptions';
import { useNotifications } from 'features/notifications/hooks/useNotifications';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { Typography } from '@mui/material';
import { NotificationSettingTypes } from 'features/notifications/notifications.constants';
import { HttpStatusCode, isAxiosError } from 'axios';

type UseGeoEventNotifications = {
  isSubscribedToNotifications: boolean;
  showSubscribeToSilentNotifications: boolean;
  toggleNotificationsSubscription: () => void;
  subscribeToSilentNotifications: () => void;
};

export const OPT_IN_TOPIC_TYPE = 'incident_topic';
export const OPT_OUT_TOPIC_TYPE = 'geo_event_opt_out';

const getTopicByType = (topics: Topic[], type: string): Topic | undefined => {
  return topics.find((topic) => topic.type === type);
};

const getIsPrescribed = (geoEvent: GenericGeoEvent): boolean => {
  if (geoEvent.geoEventType === 'wildfire') {
    return (geoEvent as Wildfire).data.isPrescribed;
  }
  return false;
};

export const isSubscribed = (
  hasOptOut: boolean,
  hasOptIn: boolean,
  geoEvent: GenericGeoEvent,
  regionSetting: NotificationOption | null,
): boolean => {
  const isPrescribed = getIsPrescribed(geoEvent);
  const { notificationType } = geoEvent;
  if (
    isPrescribed ||
    !regionSetting ||
    regionSetting === NotificationSettingTypes.off
  ) {
    return hasOptIn;
  }
  if (regionSetting === NotificationSettingTypes.allWithSilent) {
    return !hasOptOut;
  }
  if (
    regionSetting === NotificationSettingTypes.onlyNewWithSilent ||
    regionSetting === NotificationSettingTypes.onlyNew
  ) {
    return hasOptIn;
  }
  if (
    regionSetting === NotificationSettingTypes.all &&
    notificationType === NotificationType.Silent
  ) {
    return hasOptIn;
  }
  if (regionSetting === NotificationSettingTypes.all) {
    return !hasOptOut;
  }
  // shouldn't get here
  return false;
};

export const useGeoEventNotifications = (
  geoEvent: GenericGeoEvent,
): UseGeoEventNotifications => {
  const { t } = useTranslation();
  const { subscribeToTopic, unsubscribe, getSubscriptionToTopic } =
    useTopicSubscriptions();
  const { getMostCoverageSetting, putNotificationSettings } =
    useNotifications();

  const regionSetting = getMostCoverageSetting(geoEvent.regions);
  const anyRegionSubscription =
    Boolean(regionSetting) && regionSetting !== NotificationSettingTypes.off;

  // These topics are guaranteed to exist in the GeoEvent model but not in the QA environment
  const optInTopic = getTopicByType(geoEvent.topics, OPT_IN_TOPIC_TYPE);
  const optOutTopic = getTopicByType(geoEvent.topics, OPT_OUT_TOPIC_TYPE);

  const optInTopicSubscription = optInTopic
    ? getSubscriptionToTopic(optInTopic.id)
    : null;
  const optOutTopicSubscription = optOutTopic
    ? getSubscriptionToTopic(optOutTopic.id)
    : null;

  const subscribedToNotifications = isSubscribed(
    Boolean(optOutTopicSubscription),
    Boolean(optInTopicSubscription),
    geoEvent,
    regionSetting,
  );

  /**
   * Track subscription status in useState() here to optimistically reflect user
   * actions, but then reset it to the real value, determined by isSubscribed(),
   * whenever it changes (using the useEffect()).
   */
  const [isSubscribedOptimistic, setIsSubscribedOptimistic] = useState(
    subscribedToNotifications,
  );

  useEffect(() => {
    setIsSubscribedOptimistic(subscribedToNotifications);
  }, [subscribedToNotifications]);

  const enableNotifications = (): void => {
    // Optimistic updates
    setIsSubscribedOptimistic(true);

    enqueueSnackbar(
      <Typography variant="body2">
        <Trans
          i18nKey="geoEvent.notifications.successMessage.on"
          values={{ incidentName: geoEvent.name }}
        >
          Notifications <b>turned on</b> for the <b>Cloverdale Fire</b>
        </Trans>
      </Typography>,
      { variant: 'success' },
    );

    if (optOutTopicSubscription) {
      // This condition is met when the user has subscribed to the notifications of the
      // county where the geo event is located, and they have previously turned off
      // notifications for this specific geo event.
      unsubscribe.mutate(optOutTopicSubscription.id, {
        onError: (error) => {
          // Treat 404 like a success because the TopicSubscription is already removed
          if (isAxiosError(error) && error.status !== HttpStatusCode.NotFound) {
            setIsSubscribedOptimistic(false);
          }
        },
      });
    } else if (optInTopic) {
      // Subscribe to the geo event opt-in topic - turn on notifications
      subscribeToTopic.mutate(optInTopic.id, {
        onError: () => {
          setIsSubscribedOptimistic(false);
        },
      });
    }
  };

  const disableNotifications = (): void => {
    // Optimistic updates
    setIsSubscribedOptimistic(false);

    enqueueSnackbar(
      <Typography variant="body2">
        <Trans
          i18nKey="geoEvent.notifications.successMessage.off"
          values={{ incidentName: geoEvent.name }}
        >
          Notifications <b>turned off</b> for the <b>Cloverdale Fire</b>
        </Trans>
      </Typography>,
      { variant: 'success' },
    );

    if (optInTopicSubscription) {
      // This condition is met when the user has not subscribed to the notifications of the
      // county where the geo event is located and they have previously turn on
      // notifications for this specific geo event.
      unsubscribe.mutate(optInTopicSubscription.id, {
        onError: (error) => {
          // Treat 404 like a success because the TopicSubscription is already removed
          if (isAxiosError(error) && error.status !== HttpStatusCode.NotFound) {
            setIsSubscribedOptimistic(true);
          }
        },
      });
    } else if (optOutTopic) {
      // Subscribe to the geo event opt-out topic - turn off notifications
      subscribeToTopic.mutate(optOutTopic.id, {
        onError: () => {
          setIsSubscribedOptimistic(true);
        },
      });
    }
  };

  const toggleNotificationsSubscription = (): void => {
    if (isSubscribedOptimistic) {
      disableNotifications();
    } else {
      enableNotifications();
    }
  };

  const subscribeToSilentNotifications = (): void => {
    // this should only be used if there is a single region
    if (regionSetting === NotificationSettingTypes.off) return;
    const newSetting =
      regionSetting === NotificationSettingTypes.all
        ? NotificationSettingTypes.allWithSilent
        : NotificationSettingTypes.onlyNewWithSilent;
    putNotificationSettings.mutate(
      [
        {
          region: geoEvent.regions[0],
          setting: newSetting,
        },
      ],
      {
        onSuccess: () => {
          enqueueSnackbar(t('geoEvent.notifications.successMessage.silent'), {
            variant: 'success',
          });
        },
      },
    );
  };

  const isPrescribed = getIsPrescribed(geoEvent);
  const showSubscribeToSilentNotifications =
    geoEvent.regions.length === 1 &&
    anyRegionSubscription &&
    geoEvent.notificationType === NotificationType.Silent &&
    !isPrescribed &&
    !(
      regionSetting === NotificationSettingTypes.allWithSilent ||
      regionSetting === NotificationSettingTypes.onlyNewWithSilent
    );

  return {
    isSubscribedToNotifications: isSubscribedOptimistic,
    showSubscribeToSilentNotifications,
    toggleNotificationsSubscription,
    subscribeToSilentNotifications,
  };
};
